import type { Composition } from '@medplum/fhirtypes'
import type { ExtendedComposition } from '@seen/core/models/compositions/composition'
import { NoteLabel, readNoteLabelReferences } from '@seen/core/models/noteLabel'
import { readVisitNoteCategoryTokens } from '@seen/core/models/visitNoteCategory'
import { isSameDay } from 'date-fns'
import { isEqual, sortBy } from 'lodash-es'
import type { ReactNode } from 'react'
import { CategoryChangeAction } from '#src/components/activity/CategoryChangeAction.tsx'
import { LabelChangeAction } from '#src/components/activity/LabelChangeAction.tsx'
import type { LabelChangeData } from '#src/utils/GroupLabelActivityEntries.tsx'

export interface CompositionActivityEvent {
  action: ReactNode
  composition: ExtendedComposition
  // Set on label/category entries so multiple change entries from a single
  // version don't collide on the version-derived render key.
  key?: string
  // Present on label-change entries; consumed by
  // `groupCompositionLabelActivityEntries` to collapse rapid same-author edits.
  // Category entries carry no equivalent — categories are a small fixed registry
  // toggled one at a time, so they aren't grouped; their delta lives in `action`.
  labelChange?: LabelChangeData
}

/** Noun used in the "created the …" activity label. */
export type CompositionActivityNoun = 'assessment' | 'note'

/*
 * Collapses a Composition's `_history` versions into a deduped activity feed of
 * significant events: created, signed, edited, and label/category changes.
 * Consecutive same-author edits within the same day are merged into a single
 * "edited" entry so a long editing session shows up once per day rather than
 * once per autosave.
 *
 * Label and category changes are diffed independently of the content-edit
 * collapse (mirroring how the task feed diffs labels off version history), so a
 * chip change never swallows or is swallowed by a same-day content edit. Both
 * kinds of event are returned sorted ascending by `lastUpdated` so
 * `groupCompositionLabelActivityEntries` sees consecutive label edits adjacent.
 *
 * Shared by assessments and visit notes — both are `Composition`s — so the two
 * surfaces render the exact same activity UX. `resourceNoun` only customizes the
 * "created the …" label.
 */
export function buildCompositionActivity(
  pastCompositions: ExtendedComposition[],
  resourceNoun: CompositionActivityNoun
): CompositionActivityEvent[] {
  const sorted = sortBy(pastCompositions, (c) => c.lastUpdated)
  const events = [
    ...buildContentEvents(sorted, resourceNoun),
    ...buildLabelAndCategoryEvents(sorted),
  ]
  return sortBy(events, (event) => event.composition.lastUpdated)
}

function buildContentEvents(
  sorted: ExtendedComposition[],
  resourceNoun: CompositionActivityNoun
): CompositionActivityEvent[] {
  return sorted.reduce<CompositionActivityEvent[]>((acc, curr) => {
    if (acc.length === 0) {
      acc.push({
        action: curr.signedOn
          ? `created & signed the ${resourceNoun}`
          : `created the ${resourceNoun}`,
        composition: curr,
      })
      return acc
    }

    const prev = acc[acc.length - 1]

    if (curr.signedOn && curr.signedOn !== prev.composition.signedOn) {
      acc.push({ action: 'signed', composition: curr })
      return acc
    }

    if (!isEqual(prev.composition.sections, curr.sections)) {
      const didDifferentPractitionerEdit =
        prev.composition.lastUpdatedBy.reference !==
        curr.lastUpdatedBy.reference
      if (didDifferentPractitionerEdit) {
        acc.push({ action: 'edited', composition: curr })
        return acc
      }

      if (prev.action === 'signed') {
        acc.push({ action: 'edited', composition: curr })
        return acc
      }

      // We only want to show one edit per day, so we add an entry if we've moved
      // to a different day or replace the last edit otherwise.
      if (!isSameDay(prev.composition.lastUpdated, curr.lastUpdated)) {
        acc.push({ action: 'edited', composition: curr })
        return acc
      } else if (prev.action === 'edited') {
        acc[acc.length - 1] = { action: 'edited', composition: curr }
        return acc
      }
    }

    return acc
  }, [])
}

/*
 * Diffs `meta.tag` note labels and `Composition.category` visit-note categories
 * across consecutive versions, emitting one entry per changed field. Starts at
 * the second version so labels/categories present at creation aren't reported as
 * additions (matching how the first version is only ever "created").
 */
function buildLabelAndCategoryEvents(
  sorted: ExtendedComposition[]
): CompositionActivityEvent[] {
  // Convert once per version: toFhirResource() clones, and each middle version
  // is read as both prev and curr.
  const resources = sorted.map((composition) => composition.toFhirResource())
  const events: CompositionActivityEvent[] = []
  for (let i = 1; i < sorted.length; i++) {
    const labelEvent = diffLabels(resources[i - 1], resources[i], sorted[i])
    if (labelEvent) {
      events.push(labelEvent)
    }
    const categoryEvent = diffCategories(
      resources[i - 1],
      resources[i],
      sorted[i]
    )
    if (categoryEvent) {
      events.push(categoryEvent)
    }
  }
  return events
}

interface DiffTokensResult {
  added: string[]
  removed: string[]
}

function diffTokens(prev: string[], curr: string[]): DiffTokensResult {
  return {
    added: curr.filter((token) => !prev.includes(token)),
    removed: prev.filter((token) => !curr.includes(token)),
  }
}

function diffLabels(
  prev: Composition,
  curr: Composition,
  composition: ExtendedComposition
): CompositionActivityEvent | undefined {
  const { added, removed } = diffTokens(
    readNoteLabelReferences(prev.meta?.tag),
    readNoteLabelReferences(curr.meta?.tag)
  )
  if (added.length === 0 && removed.length === 0) {
    return undefined
  }
  return {
    action: (
      <LabelChangeAction
        added={added}
        removed={removed}
        LabelClass={NoteLabel}
      />
    ),
    composition,
    key: `${composition.versionId}-labels`,
    labelChange: { added, removed, LabelClass: NoteLabel },
  }
}

function diffCategories(
  prev: Composition,
  curr: Composition,
  composition: ExtendedComposition
): CompositionActivityEvent | undefined {
  const { added, removed } = diffTokens(
    readVisitNoteCategoryTokens(prev.category),
    readVisitNoteCategoryTokens(curr.category)
  )
  if (added.length === 0 && removed.length === 0) {
    return undefined
  }
  return {
    action: <CategoryChangeAction added={added} removed={removed} />,
    composition,
    key: `${composition.versionId}-categories`,
  }
}
